import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import { deleteDocument, saveData, uniqueID } from "../firebase/utility";

/**
 * Normalizes various timestamp formats (Firestore Timestamp, ISO string, milliseconds) into a numeric ms timestamp.
 */
export const normalizeNotificationTimestamp = (val) => {
  if (!val) return 0;
  if (typeof val === "number") return val;
  if (typeof val.toMillis === "function") return val.toMillis();
  if (typeof val.toDate === "function") return val.toDate().getTime();
  if (val.seconds || val._seconds) {
    return (val.seconds || val._seconds) * 1000;
  }
  const parsed = Date.parse(val);
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Visual styling / labels for different notification categories
 */
export const NOTIFICATION_CATEGORIES = {
  order: { label: "Sales Order", color: "#0284c7", bg: "#e0f2fe", icon: "FiShoppingBag" },
  purchase_order: { label: "Purchase Order", color: "#7c3aed", bg: "#ede9fe", icon: "FiFileText" },
  quote: { label: "Quote", color: "#d97706", bg: "#fef3c7", icon: "FiDollarSign" },
  product: { label: "Product", color: "#0d9488", bg: "#ccfbf1", icon: "FiBox" },
  customer: { label: "Customer", color: "#2563eb", bg: "#dbeafe", icon: "FiUsers" },
  vendor: { label: "Vendor", color: "#4f46e5", bg: "#e0e7ff", icon: "FiTruck" },
  leave: { label: "Leave Request", color: "#ea580c", bg: "#ffedd5", icon: "FiCalendar" },
  system: { label: "System", color: "#475569", bg: "#f1f5f9", icon: "FiSettings" },
};

export const getCategoryMeta = (category) => {
  return (
    NOTIFICATION_CATEGORIES[category] || {
      label: "General",
      color: "#475569",
      bg: "#f1f5f9",
      icon: "FiBell",
    }
  );
};

/**
 * Creates and stores a new notification in Firestore "Notifications" collection.
 * Non-blocking by design — errors are logged without interrupting the caller's operation.
 */
export const createAdminNotification = async ({
  title = "System Update",
  message = "",
  category = "system",
  entityId = "",
  targetUrl = "",
  actor = null,
  type = "user", // "user" ensures backward compatibility with existing admin filtering
  targetRole = "admin",
  metadata = {},
} = {}) => {
  try {
    const generatedId = typeof uniqueID === "function" ? uniqueID() : null;
    const _id = generatedId || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
    const createdAt = Date.now();

    const actorSnapshot = actor
      ? {
          id: actor._id || actor.id || "",
          name: actor.username || actor.name || "Admin",
          email: actor.email || "",
          role: actor.role || "Admin",
        }
      : null;

    const notificationPayload = {
      _id,
      title,
      message,
      category,
      type, // "user" so standard admin role checks display it
      entityId,
      targetUrl,
      read: false,
      createdAt,
      actor: actorSnapshot,
      targetRole,
      metadata,
    };

    await saveData("Notifications", _id, notificationPayload);
    return notificationPayload;
  } catch (error) {
    console.warn("Failed to create admin notification:", error?.message || error);
    return null;
  }
};

/**
 * Mark a single notification as read
 */
export const markNotificationAsRead = async (notificationId) => {
  if (!notificationId) return false;
  try {
    return await saveData("Notifications", notificationId, { read: true });
  } catch (error) {
    console.warn("Failed to mark notification as read:", error);
    return false;
  }
};

/**
 * Mark a single notification as unread
 */
export const markNotificationAsUnread = async (notificationId) => {
  if (!notificationId) return false;
  try {
    return await saveData("Notifications", notificationId, { read: false });
  } catch (error) {
    console.warn("Failed to mark notification as unread:", error);
    return false;
  }
};

/**
 * Bulk mark all unread notifications as read
 */
export const markAllNotificationsAsRead = async (notifications = []) => {
  try {
    const unread = (notifications || []).filter((n) => !n.read && (n._id || n.id));
    if (unread.length === 0) return true;

    const db = firebase.firestore();
    const batch = db.batch();

    unread.forEach((item) => {
      const docId = item._id || item.id;
      const ref = db.collection("Notifications").doc(docId);
      batch.update(ref, { read: true });
    });

    await batch.commit();
    return true;
  } catch (error) {
    console.warn("Failed to mark all notifications as read:", error);
    return false;
  }
};

/**
 * Delete a notification
 */
export const deleteNotification = async (notificationId) => {
  if (!notificationId) return false;
  try {
    await deleteDocument("Notifications", notificationId);
    return true;
  } catch (error) {
    console.warn("Failed to delete notification:", error);
    return false;
  }
};
